import { useEffect, useState } from 'react';
import { format, useNumberFormat } from '@react-input/number-format';
import copy from 'copy-to-clipboard';
import IconEye from '../icons/eye';
import IconEyeOff from '../icons/eye-off';
import IconCopyTwo from '../icons/copy-two';

// types
import { InputProps, InputSize, InputType } from './types';

// styles
import './input.css';

export default function Input({
  title,
  onChange,
  name,
  className = 'input-medium',
  disabled,
  placeholder,
  value,
  size,
  type,
  maxLength,
  error,
  prefix,
  suffix,
  labelClassName = '',
  onBlur,
  onKeyUp,
  tabIndex,
  onFocus,
  inputRef,
  amountInput,
  currency,
  min,
  max,
  prefixClassName = '',
  suffixClassName = '',
  withoutMinDigits,
  withCopy,
  withShowButton,
}: InputProps): React.JSX.Element {
  const [defaultValue, setDefaultValue] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const onCopy = () => {
    if (value) {
      copy(value?.toString());

      setCopySuccess(true);

      setTimeout(() => {
        setCopySuccess(false);
      }, 1000);
    }
  };

  const setValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      if (type === InputType.NUMBER) {
        if (
          (e.target.value[0] === '0' &&
            e.target.value[1] &&
            e.target.value[1] !== '.') || // 00 || 01
          e.target.value[0] === '.' || // .000
          Array.from(e.target.value.matchAll(/\./g)).length > 1 || // 11..21.
          e.target.value.split('.')[1]?.length > 2 || // 11.22
          Number(e.target.value) > 9_999_999_999_999_999n
        ) {
          return undefined;
        }
        let inputValue = e.target.value.replace(',', '.');
        inputValue = inputValue.replace(/[^\d.]/g, '');

        onChange({
          ...e,
          target: {
            ...e.target,
            value: inputValue,
          },
        });
      } else {
        onChange(e);
      }
    }

    return undefined;
  };

  const setAmountInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDefaultValue(e.target.value);

    if (onChange) {
      onChange({
        ...e,
        target: {
          ...e.target,
          value: e.target.value?.replaceAll(',', ''),
        },
      });
    }
  };

  let inputType = InputType.TEXT;

  if (type !== InputType.NUMBER) {
    if (type === InputType.PASSWORD) {
      inputType = passwordVisible ? InputType.TEXT : type;
    } else if (type) {
      inputType = type;
    }
  }

  useEffect(() => {
    if (value?.toString() && amountInput && (!defaultValue || disabled)) {
      if (currency) {
        if (!currency.decimals) {
          setDefaultValue(
            format(value, {
              locales: 'en',
              format: 'decimal',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
          );
        } else {
          setDefaultValue(
            format(value, {
              locales: 'en',
              format: 'decimal',
              minimumFractionDigits: withoutMinDigits ? 0 : 2,
              maximumFractionDigits: currency?.decimals,
            }),
          );
        }
      }
    }
  }, [value, amountInput, defaultValue, withoutMinDigits, disabled]);

  useEffect(() => {
    if (
      value?.toString() &&
      defaultValue &&
      amountInput &&
      currency &&
      value?.toString() !== defaultValue
    ) {
      if (currency) {
        if (!currency.decimals) {
          setDefaultValue(
            format(value, {
              locales: 'en',
              format: 'decimal',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
          );
        } else {
          setDefaultValue(
            format(value, {
              locales: 'en',
              format: 'decimal',
              minimumFractionDigits: withoutMinDigits ? 0 : 2,
              maximumFractionDigits: currency?.decimals,
            }),
          );
        }
      }
    }
  }, [value, defaultValue, withoutMinDigits]);

  const amountInputRef = useNumberFormat({
    locales: 'en',
    minimumFractionDigits:
      withoutMinDigits || (currency && !currency.decimals) ? 0 : 2,
    format: 'decimal',
    maximumFractionDigits: currency ? currency?.decimals : 2,
  });

  const passwordButtonIcon = passwordVisible ? (
    <IconEyeOff width={20} height={20} className="icon-medium icon-eye" />
  ) : (
    <IconEye width={20} height={20} className="icon-medium icon-eye" />
  );

  return (
    <label
      htmlFor={`input-${name}`}
      className={`input-label input-${size as InputSize} ${labelClassName}`}
      tabIndex={-1}
    >
      {title && <div className="input-title">{title}</div>}
      <span className={`input-wrapper ${disabled ? 'disabled' : ''}`}>
        {prefix && <div className={`prefix ${prefixClassName}`}>{prefix}</div>}
        <input
          ref={amountInput ? amountInputRef : inputRef}
          type={inputType}
          name={name}
          placeholder={placeholder}
          className={`input ${className || ''} ${error ? 'error' : ''}`}
          id={`input-${name}`}
          onChange={(e) => (amountInput ? setAmountInputValue(e) : setValue(e))}
          onBlur={onBlur}
          onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) =>
            onKeyUp && onKeyUp(event)
          }
          onFocus={onFocus}
          value={amountInput ? defaultValue : value}
          maxLength={maxLength}
          min={min}
          max={max}
          disabled={disabled}
          tabIndex={tabIndex}
          autoComplete="off"
        />
        {type === InputType.PASSWORD && withShowButton && (
          <button
            type="button"
            className={`show-password-button ${withCopy ? 'more-right' : ''}`}
            onClick={() => setPasswordVisible(!passwordVisible)}
            tabIndex={-1}
          >
            {passwordButtonIcon}
          </button>
        )}
        {withCopy && (
          <button type="button" className="copy-button" onClick={onCopy}>
            {copySuccess && <div className="copy-success">Copied!</div>}
            <IconCopyTwo width={18} height={18} />
          </button>
        )}
        {suffix && <div className={`suffix ${suffixClassName}`}>{suffix}</div>}
      </span>
      {error && <div className="error-message">{error}</div>}
    </label>
  );
}
